import React from 'react';
import { 
  File, 
  Folder, 
  Trash2, 
  HardDrive, 
  AlertTriangle,
  TrendingUp,
  BarChart2,
  Clock,
  Shield,
  CheckCircle,
  XCircle,
  Info,
  Zap,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Tooltip,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

// Components
const CustomAlert = ({ children, type }) => {
  const alertStyles = {
    warning: "bg-amber-900/30 border-amber-600/50 text-amber-200",
    error: "bg-red-900/30 border-red-600/50 text-red-200",
    success: "bg-green-900/30 border-green-600/50 text-green-200",
    info: "bg-blue-900/30 border-blue-600/50 text-blue-200",
  };

  return (
    <div className={`p-4 rounded-lg border ${alertStyles[type]} mb-4`}>
      {children}
    </div>
  );
};

const SkeletonLoader = () => (
  <div className="space-y-3 animate-pulse">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="h-10 bg-gray-700/60 rounded-md"></div>
    ))}
  </div>
);

const MainContent = ({
  loading,
  isLoading,
  selectedTarget,
  selectedFiles,
  selectedFolders,
  selectedDisks,
  fileList,
  folderList,
  diskList,
  message,
  handleFileClick,
  handleFolderInteraction,
  selectDisk,
  handleProcessClick, 
  alert,
  handleAlertClose,
  deletionStats,
  performanceData,
  deleteModesConfig,
  deleteMode
}) => {
  // Calculate success percentage for the donut chart
  const successRate = parseFloat(deletionStats.successRate.replace('%', ''));
  const pieData = [
    { name: 'Success', value: successRate },
    { name: 'Failure', value: 100 - successRate }
  ];
  const COLORS = ['#10B981', '#EF4444'];

  // Format data for quick stats at a glance
  const quickStats = [
    { 
      label: 'Deletion Rate', 
      value: deletionStats.deletionRate, 
      change: '+12%', 
      icon: Zap, 
      color: 'bg-gradient-to-r from-indigo-500 to-purple-600',
      iconColor: 'text-indigo-300'
    },
    { 
      label: 'Target Processed', 
      value: deletionStats.filesProcessed, 
      change: '+5%', 
      icon: File, 
      color: 'bg-gradient-to-r from-blue-500 to-cyan-600',
      iconColor: 'text-blue-300'
    },
    { 
      label: 'Success Rate', 
      value: deletionStats.successRate, 
      change: '+0.5%', 
      icon: CheckCircle, 
      color: 'bg-gradient-to-r from-emerald-500 to-green-600',
      iconColor: 'text-emerald-300'
    },
    { 
      label: 'Total Size', 
      value: deletionStats.totalSize, 
      change: '', 
      icon: HardDrive, 
      color: 'bg-gradient-to-r from-amber-500 to-orange-600',
      iconColor: 'text-amber-300'
    },
    { 
      label: 'Failures', 
      value: deletionStats.failures, 
      change: '', 
      icon: XCircle, 
      color: 'bg-gradient-to-r from-rose-500 to-red-600',
      iconColor: 'text-rose-300'
    },
  ];
  
  // Target Selection border colors
  const targetColors = {
    "Files": "border-blue-600 text-blue-400",
    "Folders": "border-green-600 text-green-400",
    "Disk": "border-orange-600 text-orange-400"
  };

  // Additional activity log data (mock for demonstration)
  const activityLogItems = [
    { time: "10:23 AM", action: "Secure deletion completed", status: "success", details: "12 files (452 MB)" },
    { time: "9:45 AM", action: "System scan initiated", status: "info", details: "Full system scan" },
    { time: "Yesterday", action: "Failed deletion attempt", status: "error", details: "System files protected" },
    { time: "Yesterday", action: "Disk cleanup completed", status: "success", details: "1.2 GB recovered" }
  ];

  return (
    <main className="flex-1 p-6 ml-72 bg-gray-950 text-white relative">
      {/* Decorative elements for modern design */}
      <div className="absolute top-0 right-0 w-1/3 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-1/4 h-64 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>
      
      {/* Breadcrumb */}
      <div className="flex items-center text-sm text-gray-400 mb-6">
        <span>Dashboard</span>
        <ChevronRight size={14} className="mx-2" />
        <span>Secure Deletion</span>
        <ChevronRight size={14} className="mx-2" />
        <span className="text-white">{selectedTarget}</span>
      </div>

      {/* Page Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Secure Deletion Dashboard</h1>
        <div className="flex items-center space-x-1 bg-gray-900 rounded-lg p-1 border border-gray-800">
          <button className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white">Dashboard</button>
          <button className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-400 hover:bg-gray-800">Analytics</button>
          <button className="px-3 py-1.5 rounded-md text-sm font-medium text-gray-400 hover:bg-gray-800">History</button>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        {quickStats.map((stat, index) => (
          <div key={index} className="relative overflow-hidden group">
            <div className={`${stat.color} p-6 rounded-lg shadow-lg h-full border border-gray-800/50 hover:shadow-xl transition-all duration-500`}>
              <div className="absolute top-0 right-0 p-2 opacity-10">
                <stat.icon size={50} className="text-white" />
              </div>
              <div className="flex flex-col h-full justify-between">
                <div className={`inline-flex items-center justify-center p-2 rounded-full ${stat.iconColor} bg-white/10 mb-3`}>
                  <stat.icon size={18} />
                </div>
                <div className="text-gray-300 text-sm font-medium mb-1">{stat.label}</div>
                <div className="text-2xl font-bold text-white mb-2">{stat.value}</div>
                {stat.change && (
                  <div className="text-green-400 text-sm font-medium">{stat.change}</div>
                )}
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
        ))}
      </div>

      {/* Alert Message */}
      {alert && <Alert type={alert.type} message={alert.message} onClose={handleAlertClose} />}

      {/* Target Selection Area - with modern styles and animations */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="space-y-2">
          {/* Target Selection Area */}
          {selectedTarget === "Files" && (
            <div className={`space-y-4 p-8 rounded-2xl border-2 border-dashed bg-gray-900/70 backdrop-blur-xl shadow-xl transition-all duration-500 ${loading ? "border-gray-600" : targetColors.Files}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <File className={`mr-2 ${loading ? "text-gray-500" : "text-blue-400"}`} />
                  File Selection
                </h3>
                <span className="px-3 py-1 bg-blue-900/30 text-blue-300 rounded-full text-xs font-medium">
                  {selectedFiles.length} selected
                </span>
              </div>

              {loading ? (
                <SkeletonLoader /> 
              ) : (
                <>
                  {selectedFiles.length > 0 && message && (
                    <p className="text-gray-400 text-sm">{message}</p>
                  )}
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2 thin-scrollbar">
                    {fileList.map((file, index) => (
                      <div
                        key={index}
                        onClick={() => handleFileClick(file)}
                        className={`group cursor-pointer text-left px-4 py-2.5 rounded-lg flex items-center justify-between transition-all duration-300 ${
                          selectedFiles.some(selectedFile => selectedFile.name === file.name) 
                            ? "bg-blue-600/80 text-white shadow-md shadow-blue-900/40" 
                            : "hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center">
                          <File className="mr-2 text-gray-300 group-hover:text-white transition-colors" size={18} />
                          <span className="truncate max-w-[180px]">{file.name}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full transition-colors ${
                          file.is_dir === true 
                            ? selectedFiles.some(selectedFile => selectedFile.name === file.name)
                              ? 'bg-white/20 text-white' 
                              : 'bg-green-500/20 text-green-300' 
                            : file.is_dir === false 
                              ? selectedFiles.some(selectedFile => selectedFile.name === file.name)
                                ? 'bg-white/20 text-white' 
                                : 'bg-red-500/20 text-red-300' 
                              : selectedFiles.some(selectedFile => selectedFile.name === file.name)
                                ? 'bg-white/20 text-white' 
                                : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {file.is_dir === true ? 'Folder' : 
                          file.is_dir === false ? 'File' : 
                          'Volume'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Folder Selection Area */} 
          {selectedTarget === "Folders" && (
            <div className={`space-y-4 p-8 rounded-2xl border-2 border-dashed bg-gray-900/70 backdrop-blur-xl shadow-xl transition-all duration-500 ${loading ? "border-gray-600" : targetColors.Folders}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Folder className={`mr-2 ${loading ? "text-gray-500" : "text-green-400"}`} />
                  Folder Selection
                </h3>
                <span className="px-3 py-1 bg-green-900/30 text-green-300 rounded-full text-xs font-medium">
                  {selectedFolders.length} selected
                </span>
              </div>
              
              {loading ? (
                <SkeletonLoader /> 
              ) : (
                <>
                  {selectedFolders.length > 0 && message && (
                    <p className="text-gray-400 text-sm">{message}</p>
                  )}
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2 thin-scrollbar">
                    {folderList.map((folder, index) => (
                      <div
                        key={index}
                        onClick={(e) => handleFolderInteraction(e, folder)}
                        className={`group cursor-pointer text-left px-4 py-2.5 rounded-lg flex items-center justify-between transition-all duration-300 ${
                          selectedFolders.some(selectedFolder => selectedFolder.name === folder.name) 
                            ? "bg-green-600/80 text-white shadow-md shadow-green-900/40" 
                            : "hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center">
                          <Folder className="mr-2 text-gray-300 group-hover:text-white transition-colors" size={18} />
                          <span className="truncate max-w-[180px]">{folder.name}</span>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full transition-colors ${
                          folder.is_dir === true 
                            ? selectedFolders.some(selectedFolder => selectedFolder.name === folder.name)
                              ? 'bg-white/20 text-white' 
                              : 'bg-green-500/20 text-green-300' 
                            : folder.is_dir === false 
                              ? selectedFolders.some(selectedFolder => selectedFolder.name === folder.name)
                                ? 'bg-white/20 text-white' 
                                : 'bg-red-500/20 text-red-300' 
                              : selectedFolders.some(selectedFolder => selectedFolder.name === folder.name)
                                ? 'bg-white/20 text-white' 
                                : 'bg-blue-500/20 text-blue-300'
                        }`}>
                          {folder.is_dir === true ? 'Folder' : 
                          folder.is_dir === false ? 'File' : 
                          'Volume'}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Disk Selection Area */} 
          {selectedTarget === "Disk" && (
            <div className={`space-y-4 p-8 rounded-2xl border-2 border-dashed bg-gray-900/70 backdrop-blur-xl shadow-xl transition-all duration-500 ${loading ? "border-gray-600" : targetColors.Disk}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <HardDrive className={`mr-2 ${loading ? "text-gray-500" : "text-orange-400"}`} />
                  Disk Selection
                </h3>
                <span className="px-3 py-1 bg-orange-900/30 text-orange-300 rounded-full text-xs font-medium">
                  {selectedDisks.length} selected
                </span>
              </div>
              
              {loading ? (
                <SkeletonLoader /> 
              ) : (
                <>
                  {message && (
                    <p className="text-gray-400 text-sm">{message}</p>
                  )}

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-2 thin-scrollbar">
                    {diskList.map((disk, index) => (
                      <div
                        key={index}
                        onClick={() => selectDisk(disk)}
                        className={`group cursor-pointer text-left px-4 py-2.5 rounded-lg flex items-center justify-between transition-all duration-300 ${
                          selectedDisks.some(selected => selected.name === disk.name) 
                            ? "bg-orange-600/80 text-white shadow-md shadow-orange-900/40" 
                            : "hover:bg-gray-800"
                        }`}
                      >
                        <div className="flex items-center">
                          <HardDrive className="mr-2 text-gray-300 group-hover:text-white transition-colors" size={18} />
                          <span className="flex items-center">
                            <span className="truncate max-w-[120px]">{disk.name}</span>
                            {disk.label && <span className="ml-1 text-xs opacity-80">({disk.label})</span>}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <div className="mr-3 hidden sm:block">
                            <div className="w-24 bg-gray-700 rounded-full h-2">
                              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.random() * 100}%` }}></div>
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {Math.floor(Math.random() * 900) + 100} GB free
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full transition-colors ${
                            selectedDisks.some(selected => selected.name === disk.name)
                              ? 'bg-white/20 text-white' 
                              : 'bg-blue-500/20 text-blue-300'
                          }`}>
                            Volume
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Process Warning and Button */}
          <div className="mt-6 space-y-4">
            <CustomAlert type="warning">
              <div className="flex items-center space-x-3">
                <div className="bg-amber-800/50 p-2 rounded-full">
                  <AlertTriangle className="text-amber-300" size={20} />
                </div>
                <div>
                  <h4 className="font-medium text-amber-300">Warning: Permanent Action</h4>
                  <p className="text-amber-200/80 text-sm mt-1">Selected {selectedTarget} will be permanently and securely deleted using advanced encryption methods.</p>
                </div>
              </div>
            </CustomAlert>

            <button
              onClick={handleProcessClick}
              className={`w-full flex items-center justify-center py-3 px-4 rounded-xl font-medium shadow-lg transition-all duration-300 ${
                !selectedFiles.length && !selectedFolders.length && !selectedDisks.length
                  ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white hover:shadow-xl hover:shadow-red-900/30 transform hover:-translate-y-0.5'
              }`}
              disabled={!selectedFiles.length && !selectedFolders.length && !selectedDisks.length}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </div>
              ) : (
                <>
                  <Shield className="mr-2" />
                  Execute Secure Deletion for {selectedTarget}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right Column - Activity & Status */}
        <div className="space-y-6">
          {/* Security Status Card */}
      
        <div className="bg-gray-900/70 backdrop-blur-xl p-6 rounded-2xl border border-gray-800 shadow-xl">
            <div className="flex justify-between items-start">
                <h3 className="text-lg font-semibold flex items-center">
                {React.createElement(deleteModesConfig[deleteMode].icon, { className: "mr-2 text-blue-400" })}
                Security Status
                </h3>
                <span className={`px-2 py-1 ${deleteModesConfig[deleteMode].color}/30 text-${deleteModesConfig[deleteMode].color.split('-')[1]}-300 rounded-full text-xs font-medium`}>
                {deleteModesConfig[deleteMode].level} Level Security
                </span>
            </div>
            
            <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 p-4 rounded-lg">
                <div className="text-sm text-gray-400 mb-1">Method</div>
                <div className="font-medium">AES-256 | {deleteModesConfig[deleteMode].label}</div>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                <div className="text-sm text-gray-400 mb-1">Verification</div>
                <div className="font-medium flex items-center">
                    <CheckCircle size={16} className="mr-1 text-green-400" />
                    {deleteModesConfig[deleteMode].passes > 1 ? 
                    `${deleteModesConfig[deleteMode].passes}-Pass Enabled` : 
                    'Single-Pass Enabled'}
                </div>
                </div>
            </div>

            
            {/* Deletion Success Rate Chart */}
            <div className="mt-6 flex items-center">
              <div className="w-1/3">
                <ResponsiveContainer width="100%" height={100}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={40}
                      paddingAngle={5}
                      dataKey="value"
                      stroke="none"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-2/3 pl-4">
                <h4 className="font-medium">Deletion Success Rate</h4>
                <div className="text-2xl font-bold text-green-400 mb-1">{deletionStats.successRate}</div>
                <div className="text-sm text-gray-400">Last 30 days average</div>
              </div>
            </div>
          </div>
            
          {/* Recent Activity Log 
          <div className="bg-gray-900/70 backdrop-blur-xl p-6 rounded-2xl border border-gray-800 shadow-xl">
            <h3 className="text-lg font-semibold flex items-center mb-4">
              <Clock className="mr-2 text-purple-400" />
              Recent Activity
            </h3>
            
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2 thin-scrollbar">
              {activityLogItems.map((item, index) => (
                <div key={index} className="flex items-start p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors">
                  <div className={`rounded-full p-1.5 mr-3 ${
                    item.status === 'success' ? 'bg-green-900/30 text-green-400' :
                    item.status === 'error' ? 'bg-red-900/30 text-red-400' :
                    'bg-blue-900/30 text-blue-400'
                  }`}>
                    {item.status === 'success' ? <CheckCircle size={16} /> : 
                     item.status === 'error' ? <XCircle size={16} /> : 
                     <Info size={16} />}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <span className="font-medium">{item.action}</span>
                      <span className="text-xs text-gray-400">{item.time}</span>
                    </div>
                    <div className="text-sm text-gray-400 mt-1">{item.details}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>*/}
        </div>
      </div>

      
      
      {/* Analytics Section - Enhanced Charts */}
      <div className="mt-8">
        <h2 className="text-xl font-bold mb-6 flex items-center">
          <BarChart2 className="mr-2 text-blue-400" />
          Performance Analytics
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-900/70 backdrop-blur-xl p-6 rounded-2xl border border-gray-800 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium flex items-center">
                <TrendingUp className="mr-2 text-blue-400" size={18} />
                Deletion Speed Trend
              </h3>
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-400">Avg:</span>
                <span className="font-medium">{deletionStats.deletionRate}</span>
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="shadowGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.05}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  stroke="#9CA3AF" 
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 10, right: 10 }}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  axisLine={false}
                  tickLine={false}
                  padding={{ top: 10, bottom: 10 }}
                />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: 'rgba(17, 24, 39, 0.95)', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                  labelStyle={{ color: '#F9FAFB', fontWeight: 'bold', marginBottom: '4px' }}
                  itemStyle={{ color: '#9CA3AF', padding: '2px 0' }}
                />
                <Area 
                  type="monotone" 
                  dataKey="speed" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  fill="url(#speedGradient)" 
                  animationDuration={1500}
                />
                <Area 
                  type="monotone" 
                  dataKey="speed" 
                  stroke="transparent" 
                  fill="url(#shadowGradient)" 
                  animationDuration={1500}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gray-900/70 backdrop-blur-xl p-6 rounded-2xl border border-gray-800 shadow-xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium flex items-center">
                <CheckCircle className="mr-2 text-green-400" size={18} />
                Success Rate Analysis
              </h3>
              <div className="flex items-center space-x-2 text-sm">
                <span className="text-gray-400">Target:</span>
                <span className="font-medium text-green-400">{deletionStats.successRate}</span>
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                <XAxis 
                  dataKey="name" 
                  stroke="#9CA3AF" 
                  axisLine={false}
                  tickLine={false}
                  padding={{ left: 10, right: 10 }}
                />
                <YAxis 
                  stroke="#9CA3AF" 
                  axisLine={false}
                  tickLine={false}
                  padding={{ top: 10, bottom: 10 }}
                />
                <Tooltip
                  contentStyle={{ 
                    backgroundColor: 'rgba(17, 24, 39, 0.95)', 
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  }}
                  labelStyle={{ color: '#F9FAFB', fontWeight: 'bold', marginBottom: '4px' }}
                  itemStyle={{ color: '#9CA3AF', padding: '2px 0' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="successRate" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ r: 4, fill: "#10B981", strokeWidth: 2, stroke: "#064E3B" }}
                  activeDot={{ r: 6, fill: "#10B981", strokeWidth: 2, stroke: "#064E3B" }}
                  animationDuration={1500}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      
      {/* Footer Area - System Info */}
      <div className="mt-8 py-4 border-t border-gray-800 flex justify-between items-center text-sm text-gray-500">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Shield size={14} className="mr-1 text-green-500" />
            <span>System Protected</span>
          </div>
          <div className="flex items-center">
            <Clock size={14} className="mr-1" />
            <span>Last Scan: 2h ago</span>
          </div>
        </div>
        <div className="flex items-center">
          <AlertCircle size={14} className="mr-1 text-amber-500" />
          <span>v2.5.14 Update Available</span>
        </div>
      </div>
    </main>
  );
};

// Alert Component
const Alert = ({ type, message, onClose }) => {
  const alertIcons = {
    success: <CheckCircle size={20} />,
    error: <XCircle size={20} />,
    warning: <AlertTriangle size={20} />,
    info: <Info size={20} />
  };

  const alertStyles = {
    success: "bg-green-900/30 border-green-600/50 text-green-200",
    error: "bg-red-900/30 border-red-600/50 text-red-200",
    warning: "bg-amber-900/30 border-amber-600/50 text-amber-200",
    info: "bg-blue-900/30 border-blue-600/50 text-blue-200"
  };

  return (
    <div className={`p-4 rounded-lg border mb-6 ${alertStyles[type]} animate-fadeIn transition-all duration-300`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div className={`mr-3 ${
            type === 'success' ? 'text-green-400' :
            type === 'error' ? 'text-red-400' :
            type === 'warning' ? 'text-amber-400' :
            'text-blue-400'
          }`}>
            {alertIcons[type]}
          </div>
          <p>{message}</p>
        </div>
        <button 
          onClick={onClose} 
          className="text-gray-400 hover:text-white transition-colors"
          aria-label="Close alert"
        >
          <XCircle size={18} />
        </button>
      </div>
    </div>
  );
};


export default MainContent;